import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface TenantNotFoundProps {
  slug: string | null;
}

const TenantNotFound = ({ slug }: TenantNotFoundProps) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const maskPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits.replace(/^(\d{0,2})/, "($1");
    if (digits.length <= 6) return digits.replace(/^(\d{2})(\d{0,4})/, "($1) $2");
    if (digits.length <= 10) return digits.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    return digits.replace(/^(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  };
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const displayName = slug
    ? slug.charAt(0).toUpperCase() + slug.slice(1)
    : "Seu condomínio";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Preencha nome e e-mail para continuar.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          subject: `Interesse no endereço: ${slug}.condoclick.com.br`,
          message: `Tenho interesse em cadastrar o condomínio "${displayName}" no CondoClick.`,
          source: "captacao_subdomain",
        }),
      });
      if (!res.ok) throw new Error("Erro ao enviar");
      setSent(true);
    } catch {
      setError("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{ background: "linear-gradient(to bottom right, #1e1b4b, #3730a3, #4f46e5)" }}
    >
      {/* Blobs decorativos — idênticos ao hero da landing */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-400 opacity-10 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-400 opacity-10 rounded-full -translate-x-1/4 translate-y-1/4 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logos/logo-sem-fundo-branco.svg"
            alt="CondoClick"
            className="h-28 mb-1"
            style={{ filter: "drop-shadow(0 4px 24px rgba(99,102,241,0.6))" }}
          />
        </div>

        {sent ? (
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl p-8 text-center">
            <div className="bg-green-400/20 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-green-300" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Recebemos seu contato!</h2>
            <p className="text-indigo-200 text-sm mb-6">
              Em breve nossa equipe entrará em contato para ativar{" "}
              <span className="font-semibold text-white">{slug}.condoclick.com.br</span>.
            </p>
            <button
              onClick={() => window.location.href = "https://condoclick.com.br"}
              className="text-indigo-300 hover:text-white text-sm underline underline-offset-2 transition-colors"
            >
              Saiba mais sobre o CondoClick →
            </button>
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl p-8">
            {/* Badge + título */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold text-white leading-tight mb-2">
                O endereço{" "}
                <span className="text-yellow-300">{slug}.condoclick.com.br</span>{" "}
                está disponível!
              </h1>
              <p className="text-indigo-200 text-sm leading-relaxed">
                Nenhum condomínio está cadastrado aqui ainda.
                Seja o primeiro — preencha o formulário abaixo e nossa equipe
                entra em contato para ativar o seu.
              </p>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="name" className="text-indigo-100 text-sm">Nome completo *</Label>
                <Input
                  id="name"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-indigo-300 focus:border-indigo-300 focus:ring-indigo-300"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-indigo-100 text-sm">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-indigo-300 focus:border-indigo-300 focus:ring-indigo-300"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-indigo-100 text-sm">WhatsApp</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-indigo-300 focus:border-indigo-300 focus:ring-indigo-300"
                />
              </div>

              {error && (
                <p className="text-red-300 text-sm text-center">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold py-2.5 mt-1 shadow-lg shadow-orange-500/30 transition-all"
                disabled={loading}
              >
                {loading ? "Enviando..." : "Quero cadastrar meu condomínio"}
              </Button>
            </form>

            <p className="text-center text-indigo-400 text-xs mt-4">
              Ao enviar, você concorda em receber contato da equipe CondoClick.
            </p>
          </div>
        )}

        <p className="text-center text-indigo-400 text-xs mt-5">
          <a href="https://condoclick.com.br" className="hover:text-white transition-colors">
            condoclick.com.br
          </a>
        </p>
      </div>
    </div>
  );
};

export default TenantNotFound;
